import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Banknote, CheckCircle2 } from "lucide-react";

const PROVINCES = [
  ["AB", "Alberta"], ["BC", "British Columbia"], ["MB", "Manitoba"], ["NB", "New Brunswick"],
  ["NL", "Newfoundland and Labrador"], ["NS", "Nova Scotia"], ["NT", "Northwest Territories"],
  ["NU", "Nunavut"], ["ON", "Ontario"], ["PE", "Prince Edward Island"], ["QC", "Quebec"],
  ["SK", "Saskatchewan"], ["YT", "Yukon"],
];

export const PayoutProfileCard = () => {
  const { user, setUser } = useAuth();
  const [email, setEmail] = useState(user?.payout_email || "");
  const [name, setName] = useState(user?.payout_name || user?.name || "");
  const [province, setProvince] = useState(user?.payout_province || "");
  const [busy, setBusy] = useState(false);
  const configured = Boolean(user?.payout_name);

  const save = async () => {
    if (!name) {
      toast.error("Enter a recipient name.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.put("/me/payout-profile", {
        payout_email: email || null, payout_name: name, payout_province: province || null,
      });
      setUser(data);
      toast.success("Payout details saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save payout details.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="payout-profile-card" className="rounded-xl border border-black/10 bg-white p-5">
      <div className="flex items-center gap-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Banknote className="h-4.5 w-4.5" />
        </div>
        <div>
          <h3 className="font-heading text-lg leading-none" style={{ fontWeight: 700 }}>Payout details</h3>
          <p className="mt-1 text-xs text-muted-foreground">Where we send your fee, and your province (for payout eligibility).</p>
        </div>
        {configured && (
          <span data-testid="payout-configured-badge" className="ml-auto inline-flex items-center gap-1 rounded-full bg-green-100 text-green-800 px-2.5 py-1 text-xs font-semibold">
            <CheckCircle2 className="h-3.5 w-3.5" /> Saved
          </span>
        )}
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <div>
          <Label htmlFor="payout-name" className="text-xs">Recipient name</Label>
          <Input id="payout-name" data-testid="payout-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className="mt-1" />
        </div>
        <div>
          <Label htmlFor="payout-email" className="text-xs">Interac auto-deposit email</Label>
          <Input id="payout-email" data-testid="payout-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="mt-1" />
        </div>
        <div>
          <Label className="text-xs">Province</Label>
          <Select value={province || undefined} onValueChange={setProvince}>
            <SelectTrigger data-testid="payout-province-select" className="mt-1">
              <SelectValue placeholder="Select province" />
            </SelectTrigger>
            <SelectContent>
              {PROVINCES.map(([code, label]) => (
                <SelectItem key={code} value={code} data-testid={`payout-prov-${code}`}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {province === "QC" && (
        <p data-testid="qc-payout-note" className="mt-2 text-[11px] text-amber-700">
          Québec residents can only cash out via Interac e-Transfer (provincial rules).
        </p>
      )}
      <Button data-testid="save-payout-profile-btn" onClick={save} disabled={busy} className="mt-3 rounded-full">
        {busy ? "Saving…" : configured ? "Update details" : "Save details"}
      </Button>
    </div>
  );
};

export default PayoutProfileCard;
